import React, { useState } from "react";
import { toast } from "sonner";
import { TestTube, X } from "@phosphor-icons/react";
import api from "@/lib/api";
import { fmtDate } from "@/lib/constants";

const BTN = "border border-black px-3 py-1.5 text-[11px] uppercase tracking-widest transition-colors duration-100 disabled:opacity-40";

// Mark leads as test: they stop counting toward the buyer's spend, lead counts, caps and invoices.
export function MarkTestDialog({ ids, onClose, onDone }) {
  const [hide, setHide] = useState(true);
  const [busy, setBusy] = useState(false);
  const n = ids.length;
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/bulk/leads", { action: "mark_test", lead_ids: ids, hide_from_buyer: hide });
      toast.success(`Marked ${data.updated} lead${data.updated === 1 ? "" : "s"} as test`);
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't mark them");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="mark-test-dialog">
      <div className="brut-card bg-white w-full max-w-md p-5 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="font-chivo font-bold uppercase tracking-tight">Mark {n} lead{n === 1 ? "" : "s"} as test</div>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="mark-test-close"><X size={16} /></button>
        </div>
        <p className="text-xs">They stop counting toward the buyer&apos;s spend, lead counts, daily caps and invoices. You can undo this any time.</p>
        <div className="space-y-2 text-xs">
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="radio" name="test-view" checked={hide} onChange={() => setHide(true)} className="mt-0.5 accent-black" data-testid="mark-test-hide" />
            <span><b>Hide them from the buyer</b><span className="block text-zinc-600">Gone from their dashboard and lead list.</span></span>
          </label>
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="radio" name="test-view" checked={!hide} onChange={() => setHide(false)} className="mt-0.5 accent-black" data-testid="mark-test-show" />
            <span><b>Show them tagged TEST at $0</b><span className="block text-zinc-600">The buyer still sees them, but they don&apos;t count.</span></span>
          </label>
        </div>
        <p className="text-[11px] text-zinc-600">Invoices already sent don&apos;t change.</p>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className={`${BTN} hover:bg-zinc-100`} data-testid="mark-test-cancel">Cancel</button>
          <button type="button" onClick={run} disabled={busy} className={`${BTN} bg-black text-white hover:bg-zinc-800`} data-testid="mark-test-confirm">
            {busy ? "Working…" : "Mark as test"}
          </button>
        </div>
      </div>
    </div>
  );
}

export async function unmarkTest(ids, onDone) {
  try {
    const { data } = await api.post("/bulk/leads", { action: "unmark_test", lead_ids: ids });
    toast.success(`${data.updated} lead${data.updated === 1 ? "" : "s"} count again`);
    onDone?.();
  } catch (e) {
    toast.error(e.response?.data?.detail || "Couldn't undo");
  }
}

export function testMarkNote(m) {
  if (!m) return null;
  if (m.sent_as_test) return `Sent to a buyer as a test${m.by_name ? ` by ${m.by_name}` : ""} on ${fmtDate(m.at)} · buyer sees it tagged TEST`;
  return `Marked test${m.by_name ? ` by ${m.by_name}` : ""} on ${fmtDate(m.at)} · ${m.hide_from_buyer ? "hidden from buyer" : "buyer sees it tagged TEST"}`;
}

// Lead page: mark this one lead as test, or undo it.
export function LeadTestButton({ lead, onChanged }) {
  const [open, setOpen] = useState(false);
  if (lead.is_test && (!lead.test_mark || lead.test_mark.sent_as_test)) return null;
  if (lead.is_test) {
    return (
      <button onClick={() => unmarkTest([lead.id], onChanged)} data-testid="unmark-test-lead-btn"
        className="border border-black px-3 py-2 text-xs uppercase tracking-widest hover:bg-black hover:text-white inline-flex items-center gap-1">
        <TestTube size={12} weight="bold" /> Not a test
      </button>
    );
  }
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="mark-test-lead-btn"
        className="border-2 border-yellow-500 bg-yellow-50 px-3 py-2 text-xs uppercase tracking-widest hover:bg-yellow-400 inline-flex items-center gap-1">
        <TestTube size={12} weight="bold" /> Mark as test
      </button>
      {open && <MarkTestDialog ids={[lead.id]} onClose={() => setOpen(false)} onDone={onChanged} />}
    </>
  );
}
